import { createContext, useMemo, createElement, useContext, Fragment } from 'react';

var defaultLibraryKeyResolver = function (templateComponent) { return templateComponent.type || null; };
var defaults = {
    library: new Map(),
    onUnsupportedComponent: function () { return null; },
    libraryKeyResolver: defaultLibraryKeyResolver
};
/**
 * @protected
 * Internal holder for the React context driving Experience Template rendering.
 * Public usage of this context should be done through the wrapping `ExperienceTemplateRendererContext`
 * and any exported hooks.
 */
var ExperienceTemplateRendererContextImpl = createContext(defaults);
ExperienceTemplateRendererContextImpl.displayName = 'ExperienceTemplateRendererContext';
/**
 * Provider for setting up the base of the ExperienceTemplateRendererContext.
 */
var ExperienceTemplateRendererContextProvider = function (props) {
    var children = props.children, library = props.library, wrapper = props.wrapper, onUnsupportedComponent = props.onUnsupportedComponent, libraryKeyResolver = props.libraryKeyResolver;
    // Use useMemo to avoid new object creation on every render, resulting in unnecessary re-renders of consumers.
    // See: https://reactjs.org/docs/context.html#caveats
    var providerValue = useMemo(function () { return ({
        library: library,
        wrapper: wrapper,
        onUnsupportedComponent: onUnsupportedComponent,
        libraryKeyResolver: libraryKeyResolver || defaultLibraryKeyResolver
    }); }, [library, wrapper, onUnsupportedComponent, libraryKeyResolver]);
    return createElement(ExperienceTemplateRendererContextImpl.Provider, { value: providerValue }, children);
};
/**
 * React hook for getting access to the ExperienceTemplateRendererContext provied by the nearest `ExperienceTemplateRendererContextProvider`.
 */
var useExperienceTemplateRendererContext = function () {
    return useContext(ExperienceTemplateRendererContextImpl);
};

var ExperienceTemplateRenderer = function (props) {
    var _a = useExperienceTemplateRendererContext(), library = _a.library, WrapperFromContext = _a.wrapper, onUnsupportedComponent = _a.onUnsupportedComponent, libraryKeyResolver = _a.libraryKeyResolver;
    var templateComponents = props.templateComponents, WrapperFromRenderer = props.wrapper;
    var libraryKeys = Array.from(library.keys());
    var components = templateComponents.map(function (templateComponent, templateComponentIndex) {
        var metadata = templateComponent.metadata;
        var libraryKey = libraryKeyResolver(templateComponent, libraryKeys);
        if (!libraryKey) {
            return null;
        }
        var Component = library.get(libraryKey);
        if (!Component) {
            /* istanbul ignore else */
            if (onUnsupportedComponent) {
                onUnsupportedComponent(libraryKey);
            }
            return null;
        }
        var key = (metadata === null || metadata === void 0 ? void 0 : metadata.id) || templateComponentIndex;
        if (WrapperFromContext && WrapperFromRenderer) {
            return (createElement(WrapperFromContext, { key: key, templateComponent: templateComponent },
                createElement(WrapperFromRenderer, { templateComponent: templateComponent },
                    createElement(Component, { templateComponent: templateComponent }))));
        }
        if (WrapperFromContext) {
            return (createElement(WrapperFromContext, { key: key, templateComponent: templateComponent },
                createElement(Component, { templateComponent: templateComponent })));
        }
        if (WrapperFromRenderer) {
            return (createElement(WrapperFromRenderer, { key: key, templateComponent: templateComponent },
                createElement(Component, { templateComponent: templateComponent })));
        }
        return createElement(Component, { key: key, templateComponent: templateComponent });
    });
    return createElement(Fragment, null, components);
};

/**
 * Returns child TemplateComponent of parentComponent that matches provided name, null if not found
 * @param parentComponent TemplateComponent
 * @param name string
 * @returns TemplateComponent | null
 */
function getChildByName(parentComponent, name) {
    if (!parentComponent.children || parentComponent.children.length === 0) {
        return null;
    }
    var child = parentComponent.children.find(function (c) { var _a; return ((_a = c.metadata) === null || _a === void 0 ? void 0 : _a.name) && c.metadata.name === name; });
    if (!child) {
        return null;
    }
    return child;
}

/**
 * Returns de-duped manifest of component types referenced in an Experience Template. Component types not found in provided library are called out separately
 * @param template ExperienceTemplate
 * @param library ExperienceTemplateRendererComponentLibrary
 * @param libraryKeyResolver LibraryKeyResolver - optional
 * @returns suppported: string[], unsupported: string[]
 */
function getTemplateManifest(template, library, libraryKeyResolver) {
    if (libraryKeyResolver === void 0) { libraryKeyResolver = defaultLibraryKeyResolver; }
    var result = {
        supported: new Set(),
        unsupported: new Set()
    };
    var components = template.components;
    var libraryKeys = Array.from(library.keys());
    var getComponentManifest = function (acc, component) {
        var libraryKey = libraryKeyResolver(component, libraryKeys);
        if (!libraryKey) {
            return acc;
        }
        if (library.has(libraryKey)) {
            acc.supported.add(libraryKey);
        }
        else {
            acc.unsupported.add(libraryKey);
        }
        if (component.children && component.children.length > 0) {
            component.children.forEach(function (child) {
                getComponentManifest(result, child);
            });
        }
        return acc;
    };
    components.forEach(function (component) {
        getComponentManifest(result, component);
    });
    return {
        supported: Array.from(result.supported),
        unsupported: Array.from(result.unsupported)
    };
}

export { ExperienceTemplateRenderer, ExperienceTemplateRendererContextProvider, getChildByName, getTemplateManifest };
